import type { FetchArgs } from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import getConfig from 'next/config';

import { BASE_TIMEOUT } from 'constants/apiTimeout';
import { HEADER_NAMES } from 'constants/headersNames';
import type { APP_PAGES_NAMES } from 'constants/routes';
import { UTM_MARKS } from 'constants/utmMarks';

import { isSkipRetry } from 'shared/api/isSkipRetry';
import { getOnlySelectionAbExperimentsString } from 'shared/lib/experiments/getOnlySelectionAbExperimentsString';
import { getUtmAndPageExperiments } from 'shared/lib/experiments/getUtmAndPageExperiments';
import { getUtmMarksFromQueryOrCookie } from 'shared/lib/getUtmMarksFromQueryOrCookie';

const { apiPrefix = '' } = getConfig()?.publicRuntimeConfig ?? {};

const staggeredBaseQuery = retry(
  async (args: string | FetchArgs, api, extraOptions: Record<string, unknown>) => {
    const state = api.getState() as {
      whiteLabelUserData?: { token?: string };
      abExperiments?: { experiments: Record<string, string> };
      utmMarks?: { cookie: Record<string, string>; query: Record<string, string> };
      siteSettings: { appPageName: ValuesOf<typeof APP_PAGES_NAMES> };
      user?: { deviceId?: string };
    };
    const { token: accessToken } = state?.whiteLabelUserData || {};
    const { experiments } = state?.abExperiments || {};
    const { query = {}, cookie = {} } = state?.utmMarks || {};
    const { appPageName } = state?.siteSettings || {};
    const { deviceId } = state?.user || {};
    const { [UTM_MARKS.medium]: utmMedium } = getUtmMarksFromQueryOrCookie(query, cookie);

    const result = await fetchBaseQuery({
      baseUrl: apiPrefix,
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(deviceId ? { [HEADER_NAMES.sessionId]: deviceId } : {}),
        ...(experiments
          ? {
              [HEADER_NAMES.abGroups]: getOnlySelectionAbExperimentsString(
                getUtmAndPageExperiments(experiments, appPageName, utmMedium),
              ),
            }
          : {}),
      },
      timeout: BASE_TIMEOUT,
    })(args, api, extraOptions);

    // Скипаем ретраи для кодов которые нельзя ретраить
    if (result.error) {
      let { status } = result.error;
      if ('originalStatus' in result.error) {
        status = result.error.originalStatus;
      }

      if (isSkipRetry(status) || extraOptions.maxRetries === 0) {
        retry.fail(result.error);
      }
    }

    return result;
  },
  {
    maxRetries: 2,
  },
);

export const baseRTKApi = createApi({
  /** Cache tags */
  tagTypes: [],

  /** Reduce store unique name */
  reducerPath: 'api',

  /** async function implementation, can be reimplemented with axios here */
  baseQuery: staggeredBaseQuery,

  /** custom endpoints */
  endpoints: () => ({}),
});
